import React from 'react';
import {useTranslation, Trans} from 'react-i18next';
import styled from 'styled-components';
import {Checkbox, ControlButton, TscButtonType, Glyphs, TscThemeNames, TscStyles} from '@techsmith/tsc-cloud-style-guide';
import cssConstants from '../../constants/cssConstants';
import logging from '../../service/logging';
import {StyledWarning, HyperLinkButton} from '../util/StyledElements';
import quizApi from '../../service/quizApi';
import '../../static/css/modal.less';
import conversationApi from '../../service/conversationApi';
import {IWorkerState} from '../../model/workerState';
import withMemoizedContexts from '../../context/contextContainerHoC';
import {themeStore} from '../../context/themeProvider';
import {IMediaDetails} from '../../model/mediaDetails';
import mediaApi from '../../service/mediaApi';
import {BasicModalPortal} from '../util/ModalPortalHelper';
import config from '../../service/config';

const BoldText = styled.span`
   font-weight: ${TscStyles.font.weight.semibold};
   padding: 0 0.25rem;
   display: inline-flex;
`;

const CheckboxWrapper = styled.div`
   display: flex;
   flex-direction: row;
   margin: 1rem 0 2rem 0;
   padding: 1rem;
   border: solid 1px ${TscStyles.color.action.destructive};
   background-color: rgba(205, 66, 66, .15);
   border-radius: 0.25rem;
   max-width: 22rem;
   justify-content: center;

   > div {
      margin: 0;
   }

   span {
      margin-left: 1rem;
   }
`;

const FooterButtonsWrapper = styled.div`
   display: flex;
   .button-group {
      margin-left: auto;
   }
`;

const DownloadButtonWrapper = styled.div`
   @media screen and (max-width: ${cssConstants.contentBreakpoint}px) {
      > button > span:last-of-type {
         display: none;
      }
   }
`;

export const ResetDataModalBase: React.FC<IResetDataModalProps & IStateMappedProps> = ({onClose, onResetData, selectedMedia, theme}) => {
   const {t} = useTranslation();
   const [state, setState] = React.useState({
      resetConfirmed: false,
      isWorking: false,
      hasError: false
   } as IResetDataModalState);

   const onDownloadDataClick = async () => {
      setState({...state, isWorking: true, hasError: false});

      try {
         await mediaApi.downloadBatchMediaCsv(selectedMedia.map(m => m.Hash));
         setState({...state, isWorking: false, hasError: false});
      } catch (e) {
         logging.error(e);
         setState({...state, isWorking: false, hasError: true});
      }
   };

   const onResetDataClick = async () => {
      setState({...state, isWorking: true, hasError: false});

      try {
         const removeQuizPromises = config.featureSwitches.DisableAnalyticsAndQuizEditing ? [] : selectedMedia.map(m => quizApi.removeQuizResponses(m.Hash));
         const clearConversationPromises = selectedMedia.map(m => conversationApi.clearMediaConversationComments(m.Hash));

         await Promise.all(removeQuizPromises.concat(clearConversationPromises));

         onClose();
         onResetData && onResetData();
      } catch (e) {
         logging.error(e);
         setState({...state, isWorking: false, hasError: true});
      }
   };

   const toggleResetConfirmation = (): void => {
      setState({...state, resetConfirmed: !state.resetConfirmed});
   };

   const resetWarningWithQuizMessage: React.JSX.Element = (
      <div>
         <Trans i18nKey="resetData.warningWithQuizMessage">
            All
            <BoldText>viewer data, quiz responses,</BoldText>
            and
            <BoldText>conversations</BoldText>
            will be archived and reset. If you want a local backup,
            <HyperLinkButton onClick={onDownloadDataClick}>download the media data</HyperLinkButton>
            before resetting.
         </Trans>
      </div>
   );

   const resetWarningWithoutQuizMessage: React.JSX.Element = (
      <div>
         <Trans i18nKey="resetData.warningWithoutQuizMessage">
            All
            <BoldText>viewer data</BoldText>
            and
            <BoldText>conversations</BoldText>
            will be archived and reset. If you want a local backup,
            <HyperLinkButton onClick={onDownloadDataClick}>download the media data</HyperLinkButton>
            before resetting.
         </Trans>
      </div>
   );

   const resetConfirmation: React.JSX.Element = (
      <CheckboxWrapper>
         <Checkbox testId="reset-confirmation-checkbox" isChecked={state.resetConfirmed} onClick={toggleResetConfirmation} a11Text={t('resetData.confirmation')} />
         <span>{t('resetData.confirmation')}</span>
      </CheckboxWrapper>
   );

   const downloadDataButton: React.JSX.Element = (
      <DownloadButtonWrapper>
         <ControlButton
            label={t('resetData.downloadData')}
            buttonType={TscButtonType.tertiary}
            glyph={<Glyphs.Download16x16 />}
            disabled={false}
            themeName={theme}
            onClick={onDownloadDataClick}
            testId={'reset-data-modal-download-button'}
         />
      </DownloadButtonWrapper>
   );

   const buttonGroup: React.JSX.Element = (
      <div className="button-group">
         <ControlButton
            label={t('general.cancel')}
            buttonType={TscButtonType.secondary}
            disabled={false}
            themeName={theme}
            width="5.5rem"
            onClick={onClose}
            testId={'reset-data-modal-cancel-button'}
         />
         <ControlButton
            label={t('resetData.button')}
            buttonType={TscButtonType.destructive}
            disabled={!state.resetConfirmed || state.isWorking}
            themeName={theme}
            width="5.5rem"
            onClick={onResetDataClick}
            testId={'reset-data-modal-clear-data-button'}
         />
      </div>
   );

   const hasVideo = selectedMedia.some(m => m.IsVideo);

   return (
      <BasicModalPortal visible={true} title={t('resetData.clearData')} width={cssConstants.defaultModalWidth} onClose={onClose} themeName={theme} isLoading={state.isWorking} testId="reset-data-modal">
         <div className="modal-body">
            <div className="form-body themeable-section">
               {state.hasError && <StyledWarning>{t('general.errorText')}</StyledWarning>}
               {hasVideo && !config.featureSwitches.DisableAnalyticsAndQuizEditing ? resetWarningWithQuizMessage : resetWarningWithoutQuizMessage}
               {resetConfirmation}
               <FooterButtonsWrapper>
                  {downloadDataButton}
                  {buttonGroup}
               </FooterButtonsWrapper>
            </div>
         </div>
      </BasicModalPortal>
   );
};

export interface IResetDataModalProps {
   onClose(): void;
   onResetData?(): void;
   selectedMedia: IMediaDetails[];
}

export interface IResetDataModalState extends IWorkerState {
   resetConfirmed: boolean;
}

export interface IStateMappedProps {
   theme: TscThemeNames;
}

const mapStatesToProps = (
   {onClose, onResetData, selectedMedia}: IResetDataModalProps,
   {theme}: Partial<IStateMappedProps>
): IStateMappedProps & IResetDataModalProps => ({onClose, onResetData, selectedMedia, theme});

export default withMemoizedContexts(mapStatesToProps, themeStore)(ResetDataModalBase);
